import {
  Badge,
  Button,
  Cell,
  Column,
  ConfirmModal,
  Row,
  TableFrame,
  TableHeader,
} from '@skupreme/shared/ui';
import ShippingLabelsCarriersService from '@utility/services/shipping-labels-carriers';
import { ToastMessage } from '@utility/toast';
import { useState } from 'react';
import { AddEditCredentialsPopup } from '../../legacy-bridge';
import { useProviderCarriers } from '../../hooks/use-provider-tabs';
import { credentialFields, type ProviderCarrier } from '../../lib/carriers';

const COLUMN_COUNT = 4;

function CarrierName({ carrier }: { carrier: ProviderCarrier }) {
  const fields = credentialFields(carrier);
  return (
    <div className="sk:flex sk:flex-col sk:gap-0.5">
      <span className="sk:flex sk:items-center sk:gap-2 sk:font-medium">
        {carrier.provider?.name}
        {carrier.primary ? <Badge tone="info">Default</Badge> : null}
      </span>
      {fields.map(field => (
        <span key={field.id} className="sk:text-xs">
          <span className="sk:text-fg-tertiary">{field.key}</span> {field.value}
        </span>
      ))}
    </div>
  );
}

/** The carriers this provider ships with, and the credentials it uses. */
export function CarriersTab({ providerId }: { providerId: string }) {
  const {
    data: carriers,
    loading,
    replace,
    refresh,
  } = useProviderCarriers(providerId);
  const [editing, setEditing] = useState<ProviderCarrier | null>(null);
  const [removing, setRemoving] = useState<ProviderCarrier | null>(null);

  const remove = async (carrier: ProviderCarrier) => {
    try {
      await ShippingLabelsCarriersService.removeProviderCarrierService(
        carrier.provider.value,
        { fulfillment_provider_id: providerId },
      );
      ToastMessage.success(`Credentials removed for ${carrier.provider.name}`);
      refresh();
    } catch (error) {
      ToastMessage.apiError(error);
    }
  };

  /* The form hands back the saved carrier, or nothing when cancelled. */
  const closeEditor = (saved?: ProviderCarrier) => {
    if (saved) replace(saved);
    setEditing(null);
  };

  return (
    <>
      <TableFrame
        aria-label="Carriers"
        columnCount={COLUMN_COUNT}
        isLoading={loading}
        loadingRows={3}
        emptyMessage="No carriers for this provider"
        header={
          <TableHeader>
            <Column id="carrier" isRowHeader>
              Carrier
            </Column>
            <Column id="services" className="sk:w-40">
              Services
            </Column>
            <Column id="packages" className="sk:w-40">
              Packages
            </Column>
            <Column id="actions" aria-label="Actions" className="sk:w-64">
              {''}
            </Column>
          </TableHeader>
        }
      >
        {carriers.map(carrier => (
          <Row key={carrier.provider.value} id={carrier.provider.value}>
            <Cell>
              <CarrierName carrier={carrier} />
            </Cell>
            <Cell className="sk:tabular-nums">
              {carrier.total_services ?? 0}
            </Cell>
            <Cell className="sk:tabular-nums">
              {carrier.total_packages ?? 0}
            </Cell>
            <Cell>
              <div className="sk:flex sk:items-center sk:justify-end sk:gap-2">
                <Button
                  variant="pill"
                  size="sm"
                  onPress={() => setEditing({ ...carrier })}
                >
                  {carrier.credentials ? 'Edit' : 'Set'} credentials
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  isDisabled={!carrier.credentials}
                  onPress={() => setRemoving(carrier)}
                >
                  Delete
                </Button>
              </div>
            </Cell>
          </Row>
        ))}
      </TableFrame>
      <AddEditCredentialsPopup
        open={!!editing}
        carrier={editing}
        closeModal={closeEditor}
        fulfillment_provider_id={providerId}
      />
      <ConfirmModal
        isOpen={!!removing}
        title="Delete credentials"
        onClose={() => setRemoving(null)}
        onConfirm={() => {
          if (removing) remove(removing);
          setRemoving(null);
        }}
      >
        {removing
          ? `The ${removing.provider.name} credentials of this provider will be deleted.`
          : null}
      </ConfirmModal>
    </>
  );
}
